import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { feedbackConversationAuthorityFingerprint, getFeedbackAnsweredContext, getFeedbackConversation,
  getFeedbackPublication, isCurrentFeedbackConversationScope, observeAuthTokenChanges } from '@/lib/api';
import type { FeedbackAnsweredContextResponse, FeedbackConversationResponse, FeedbackConversationScope,
  FeedbackConversationTarget, FeedbackPublicationResponse, FeedbackPublishedPin, ResourceMediaSelection,
  ResourcePin, ResourceReadScope } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';

export type FeedbackParentSource =
  | { purpose: 'staff'; scope: ResourceReadScope; feedbackId: string; expectedFeedbackRevisionId?: string }
  | { purpose: 'current'; scope: FeedbackConversationScope; target: FeedbackConversationTarget; expectedFeedbackRevisionId: string }
  | { purpose: 'answered'; scope: FeedbackConversationScope; target: FeedbackConversationTarget; messageId: string; expectedFeedbackRevisionId: string };
export type FeedbackPinSource = FeedbackParentSource & { expectedFeedbackRevisionId: string };
export function feedbackPinMediaSelection(source: FeedbackPinSource, pin: ResourcePin): ResourceMediaSelection {
  const pair = { resourceId: pin.resource_id, revisionId: pin.revision_id };
  return source.purpose === 'staff' ? { grant: 'coach', ...pair }
    : source.purpose === 'current' ? { grant: 'current', ...pair, ...source.target, view: source.scope.view,
      expectedFeedbackRevisionId: source.expectedFeedbackRevisionId }
      : { grant: 'answered', ...pair, ...source.target, view: source.scope.view, messageId: source.messageId };
}
type Parent = FeedbackPublicationResponse | FeedbackConversationResponse | FeedbackAnsweredContextResponse;
type ReadState = { key: string; attempt: number; data: Parent | null; error: boolean };

/** A pure owning read for this disclosure. It deliberately does not reuse an
 * in-flight read begun before opening, or acquire a full-view/reading proof. */
export function useFeedbackParentRead(source: FeedbackParentSource, enabled = true) {
  const key = JSON.stringify(source);
  const captured = useMemo(() => JSON.parse(key) as FeedbackParentSource, [key]);
  const boundary = useMemo(() => {
    const scope = { ...source.scope, view: source.purpose === 'staff' ? 'coach' as const : source.scope.view };
    const fingerprint = feedbackConversationAuthorityFingerprint(); let departed = false;
    const current = () => {
      if (!isCurrentFeedbackConversationScope(scope) || fingerprint !== feedbackConversationAuthorityFingerprint()) departed = true;
      return !departed;
    };
    return { current, subscribe: (notify: () => void) => {
      const changed = () => { current(); notify(); };
      const auth = useAuthStore.subscribe(changed), selection = useScopeStore.subscribe(changed), token = observeAuthTokenChanges(changed);
      changed(); return () => { auth(); selection(); token(); };
    } };
    // A caller keys this owner by actor/scope. An observed departure never revives it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const authorized = useSyncExternalStore(boundary.subscribe, boundary.current, () => false);
  const [attempt, retry] = useState(0), [state, setState] = useState<ReadState | null>(null);
  useEffect(() => {
    if (!enabled || !authorized) { setState(null); return; }
    const controller = new AbortController(); let live = true;
    setState(null);
    const options = { scope: captured.scope, signal: controller.signal };
    const read = captured.purpose === 'staff' ? getFeedbackPublication(captured.feedbackId, options)
      : captured.purpose === 'current' ? getFeedbackConversation(captured.target, { ...options, scope: captured.scope })
        : getFeedbackAnsweredContext(captured.target, captured.messageId, { ...options, scope: captured.scope });
    void read.then(data => {
      if (!live || !boundary.current() || controller.signal.aborted) return;
      const id = captured.purpose === 'staff' ? captured.feedbackId : captured.target.feedbackId;
      const matches = data.feedback_id === id
        && (!captured.expectedFeedbackRevisionId || data.feedback_revision_id === captured.expectedFeedbackRevisionId)
        && (captured.purpose === 'staff' || 'player_id' in data && data.player_id === captured.target.playerId)
        && (captured.purpose !== 'answered' || 'message_id' in data && data.message_id === captured.messageId);
      setState({ key, attempt, data: matches ? data : null, error: !matches });
    }).catch(() => {
      if (live && boundary.current() && !controller.signal.aborted) setState({ key, attempt, data: null, error: true });
    });
    return () => { live = false; controller.abort(); };
  }, [captured, key, attempt, authorized, enabled, boundary]);
  const current = enabled && authorized && state?.key === key && state.attempt === attempt;
  return { active: authorized, data: current ? state.data : null, error: current && state.error,
    loading: enabled && authorized && !current, readVersion: attempt,
    refresh: () => { if (boundary.current()) retry(value => value + 1); } };
}

export function useFeedbackPinRead(source: FeedbackPinSource, pin: ResourcePin, enabled = true) {
  const read = useFeedbackParentRead(source, enabled);
  const parent = read.data;
  const found = parent && !('availability' in parent && parent.availability === 'unavailable')
    ? parent.resource_links.find(item => item.resource_id === pin.resource_id && item.revision_id === pin.revision_id) : null;
  const resource: FeedbackPublishedPin | null = found ?? null;
  const selection = feedbackPinMediaSelection(source, pin);
  return { ...read, resource, selection, unavailable: !read.active || read.error
    || !!parent && (!resource || resource.availability === 'unavailable') };
}
